import React, { useCallback, useEffect, useRef, useState } from "react";
import type { Editor } from "@tiptap/react";
import { Copy, Eye, FileDown, FileStack, FileUp, Info, Loader2, Plus, RotateCcw, Save, Sparkles, Star, Trash2, X } from "lucide-react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { SectionsEditor, sectionProblem, toApiSections, toEditableSections, type EditableSection } from "@/components/proposals/SectionsEditor";
import { MergeFieldPanel } from "@/components/proposals/MergeFieldPanel";
import { ProposalDocument } from "@/components/proposals/ProposalDocument";
import { TemplateDesignEditor } from "@/components/proposals/TemplateDesignEditor";
import type { MergeField, ProposalDefaults, ProposalRender, ProposalSection, ProposalStyle, ProposalTemplate } from "@/types/proposal";
import { useConfirm } from "@/context/ConfirmContext";
import { useAiFeature } from "@/lib/ai";

interface Draft {
  id: string | null;
  name: string;
  description: string;
  is_default: boolean;
  status: boolean;
  sections: EditableSection[];
  /** Its own design; null is the standard look */
  style: ProposalStyle | null;
  /** What proposals made from it start with (GST, validity); kept as is unless it came from a sample */
  defaults?: ProposalDefaults | null;
}

/** A template draft read from a sample PDF (nothing is saved yet) */
interface SampleDraft {
  name: string;
  description: string;
  style: ProposalStyle;
  sections: ProposalSection[];
  defaults: ProposalDefaults;
  notes: string[];
}

const blank = (): Draft => ({
  id: null,
  name: "",
  description: "",
  is_default: false,
  status: true,
  sections: toEditableSections([
    { type: "cover", title: "Cover", html: null },
    { type: "rich_text", title: "Executive summary", html: "<p>Dear {{contact.first_name}},</p><p></p>" },
    { type: "estimate", title: "Investment (indicative)", html: "" },
    { type: "acceptance", title: "Acceptance", html: "<p>By accepting, {{customer.company}} confirms it wishes to proceed on the basis of this proposal.</p>" },
  ]),
  style: null,
});

/** Settings → Proposal Templates: the starting points reps pick when they write a proposal */
export function ProposalTemplatesPage() {
  const confirm = useConfirm();
  const { success, error } = useToast();
  const [templates, setTemplates] = useState<ProposalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [mergeFields, setMergeFields] = useState<MergeField[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [usedBy, setUsedBy] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const activeEditor = useRef<Editor | null>(null);
  const [preview, setPreview] = useState<ProposalRender | null>(null);
  const [previewError, setPreviewError] = useState(false);
  const [tab, setTab] = useState<"sections" | "design">("sections");
  const fromSampleOn = useAiFeature("TEMPLATE_FROM_SAMPLE");
  const sampleInput = useRef<HTMLInputElement | null>(null);
  const [reading, setReading] = useState(false);
  const [sampleNotes, setSampleNotes] = useState<string[] | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const list = unwrapArray<ProposalTemplate>(unwrapObject(await api.get("/api/ProposalTemplate/List?all=true")));
      setTemplates(list);
      return list;
    } catch (err: any) {
      error("Couldn't load templates", err.message || "Something went wrong");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const open = useCallback(async (id: string) => {
    try {
      const t = unwrapObject<ProposalTemplate>(await api.get(`/api/ProposalTemplate/ById/${id}`));
      if (t.merge_fields) setMergeFields(t.merge_fields);
      setDraft({ id: t.id, name: t.name, description: t.description || "", is_default: t.is_default, status: t.status, sections: toEditableSections(t.sections), style: t.style ?? null });
      setDirty(false);
      setSampleNotes(null);
      activeEditor.current = null;
    } catch (err: any) {
      error("Couldn't open the template", err.message || "Something went wrong");
    }
  }, []);

  useEffect(() => {
    loadList().then((list) => {
      const first = list.find((t) => t.is_default) || list[0];
      if (first) open(first.id);
    });
  }, []);

  useEffect(() => {
    setUsedBy(templates.find((t) => t.id === draft?.id)?.used_by || 0);
  }, [templates, draft?.id]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Live preview: the unsaved sections drawn with sample figures, a moment after the last keystroke
  useEffect(() => {
    if (!draft) return;
    let stale = false;
    const timer = setTimeout(async () => {
      try {
        const doc = unwrapObject<ProposalRender>(await api.post("/api/ProposalTemplate/Preview", { name: draft.name.trim() || "Proposal title", sections: toApiSections(draft.sections), style: draft.style }));
        if (!stale) {
          setPreview(doc);
          setPreviewError(false);
        }
      } catch {
        if (!stale) setPreviewError(true);
      }
    }, 500);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [draft?.sections, draft?.name, draft?.style]);

  const leaveOk = async () =>
    !dirty || (await confirm({ title: "Discard your changes?", message: "The unsaved changes to this template will be lost.", confirmLabel: "Discard", destructive: true }));
  const change = (patch: Partial<Draft>) => {
    setDraft((d) => (d ? { ...d, ...patch } : d));
    setDirty(true);
  };

  const save = async (override?: Partial<Draft>) => {
    if (!draft) return;
    const d = { ...draft, ...override };
    const problem = !d.name.trim() ? "Give the template a name" : sectionProblem(d.sections);
    if (problem) {
      error("Can't save yet", problem);
      return;
    }
    setBusy(true);
    try {
      const body = { name: d.name.trim(), description: d.description.trim() || null, is_default: d.is_default, status: d.status, sections: toApiSections(d.sections), style: d.style, ...(d.defaults !== undefined ? { defaults: d.defaults } : {}) };
      const res = d.id ? await api.put(`/api/ProposalTemplate/Update/${d.id}`, body) : await api.post("/api/ProposalTemplate/Insert", body);
      const t = unwrapObject<ProposalTemplate>(res);
      success("Template saved", d.id ? `"${t.name}" updated` : `"${t.name}" created`);
      await loadList();
      await open(t.id);
    } catch (err: any) {
      error("Save failed", err.message || "Could not save the template");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!draft?.id) return;
    const msg = usedBy
      ? `"${draft.name}" was used for ${usedBy} proposal${usedBy === 1 ? "" : "s"}, so it will be deactivated (hidden from new proposals) rather than deleted. Continue?`
      : `Delete "${draft.name}"? This can't be undone.`;
    if (!(await confirm({ title: usedBy ? "Deactivate this template?" : "Delete this template?", message: msg, confirmLabel: usedBy ? "Deactivate" : "Delete", destructive: true }))) return;
    setBusy(true);
    try {
      const res = unwrapObject<{ removed: boolean; deactivated: boolean }>(await api.delete(`/api/ProposalTemplate/Delete/${draft.id}`));
      success(res.deactivated ? "Template deactivated" : "Template deleted", res.deactivated ? "It no longer appears for new proposals" : `"${draft.name}" removed`);
      setDirty(false);
      const list = await loadList();
      if (res.deactivated) await open(draft.id);
      else {
        const next = list.find((t) => t.is_default) || list[0];
        if (next) await open(next.id);
        else setDraft(null);
      }
    } catch (err: any) {
      error("Couldn't remove it", err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const startNew = async () => {
    if (!(await leaveOk())) return;
    setDraft(blank());
    setDirty(true);
    activeEditor.current = null;
  };

  /** Reads a sample PDF into a new, unsaved template: its look and sections, our merge fields */
  const fromSample = async (file: File | undefined) => {
    if (!file || !(await leaveOk())) return;
    if (file.size > 15 * 1024 * 1024) {
      error("The sample is too big", "Keep it under 15 MB (the first few pages are enough)");
      return;
    }
    setReading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const d = unwrapObject<SampleDraft>(await api.upload("/api/ProposalTemplate/FromSample", fd));
      setDraft({ id: null, name: d.name, description: d.description || "", is_default: false, status: true, sections: toEditableSections(d.sections), style: d.style, defaults: d.defaults });
      setDirty(true);
      setSampleNotes(d.notes || []);
      setTab("design");
      activeEditor.current = null;
      success("Template ready to check", "Adjust the design and sections, then save");
    } catch (err: any) {
      error("Couldn't read the sample", err.message || "Please try another file");
    } finally {
      setReading(false);
      if (sampleInput.current) sampleInput.current.value = "";
    }
  };

  /** The unsaved template as the PDF customers get (sample figures, on the letterhead when it would be) */
  const openPdf = async () => {
    if (!draft) return;
    setPdfBusy(true);
    try {
      const res = await fetch("/api/ProposalTemplate/PreviewPdf", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: draft.name.trim() || "Proposal title", sections: toApiSections(draft.sections), style: draft.style }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.message || "Please try again");
      const url = URL.createObjectURL(await res.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      error("Couldn't make the PDF", err.message || "Please try again");
    } finally {
      setPdfBusy(false);
    }
  };

  const duplicate = async () => {
    if (!draft || !(await leaveOk())) return;
    setDraft({
      ...draft,
      id: null,
      name: `Copy of ${draft.name}`.slice(0, 120),
      is_default: false,
      status: true,
      sections: toEditableSections(toApiSections(draft.sections).map(({ id, ...s }) => s)),
    });
    setDirty(true);
  };

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Proposal Templates</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            The sections and wording reps start from. Merge fields like {"{{customer.company}}"} are filled in for each deal.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {fromSampleOn && (
            <>
              <input ref={sampleInput} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => void fromSample(e.target.files?.[0])} aria-label="Sample PDF" />
              <Button size="sm" variant="outline" onClick={() => sampleInput.current?.click()} disabled={reading} className="h-9 gap-1.5">
                {reading ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />} Make from a sample
              </Button>
            </>
          )}
          <Button size="sm" onClick={startNew} className="h-9 gap-1.5 shadow-sm">
            <Plus className="size-4" /> New Template
          </Button>
        </div>
      </div>

      {reading && (
        <div className="flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm" role="status">
          <Loader2 className="size-4 animate-spin text-primary" />
          <span>
            Reading the sample: its colours, fonts and sections. This can take up to a minute.
            <span className="block text-xs text-muted-foreground">The file isn't kept. Nothing is saved until you press Save.</span>
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
        {/* Template list and merge fields */}
        <div className="xl:col-span-3 space-y-4">
        <div className="bg-card border rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b text-sm font-bold flex items-center gap-2">
            <FileStack className="size-4 text-primary" /> Templates
          </div>
          {loading ? (
            <div className="p-3 space-y-2">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : (
            <ul className="divide-y">
              {draft && !draft.id && (
                <li className="px-4 py-2.5 bg-primary/5 text-sm font-semibold">{draft.name.trim() || "New template"} (unsaved)</li>
              )}
              {templates.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={async () => {
                      if (draft?.id !== t.id && (await leaveOk())) open(t.id);
                    }}
                    className={cn("w-full text-left px-4 py-2.5 hover:bg-muted/50", draft?.id === t.id && "bg-primary/5", !t.status && "opacity-60")}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold truncate">{t.name}</span>
                      {t.is_default && <Star className="size-3.5 text-warning fill-warning shrink-0" />}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {!t.status ? "Inactive · " : ""}
                      Used for {t.used_by || 0} proposal{t.used_by === 1 ? "" : "s"}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {draft && <MergeFieldPanel fields={mergeFields} activeEditor={activeEditor} showValues={false} />}
        </div>

        {/* Editor */}
        {draft ? (
          <>
            <div className="xl:col-span-5 space-y-4">
              <div className="bg-card border rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    {draft.is_default && <Badge variant="warning">Default</Badge>}
                    {!draft.status && <Badge variant="outline">Inactive</Badge>}
                    {dirty && <span className="text-xs text-warning">Unsaved changes</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {draft.id && (
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={duplicate} disabled={busy}>
                        <Copy className="size-4" /> Duplicate
                      </Button>
                    )}
                    {draft.id && !draft.status && (
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => save({ status: true })} disabled={busy}>
                        <RotateCcw className="size-4" /> Reactivate
                      </Button>
                    )}
                    {draft.id && !draft.is_default && draft.status && (
                      <Button variant="outline" size="sm" className="gap-1.5 text-destructive" onClick={remove} disabled={busy}>
                        <Trash2 className="size-4" /> {usedBy ? "Deactivate" : "Delete"}
                      </Button>
                    )}
                    <Button size="sm" className="gap-1.5" onClick={() => save()} disabled={busy || !dirty}>
                      {busy ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Name *</Label>
                    <Input value={draft.name} maxLength={120} onChange={(e) => change({ name: e.target.value })} placeholder="e.g. Implementation project" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">When to use it</Label>
                    <Input value={draft.description} maxLength={500} onChange={(e) => change({ description: e.target.value })} placeholder="Shown to reps when they pick a template" />
                  </div>
                </div>
                <label className="flex items-center gap-2.5 text-sm">
                  <Switch
                    checked={draft.is_default}
                    disabled={draft.id !== null && templates.find((t) => t.id === draft.id)?.is_default}
                    onCheckedChange={(v) => change({ is_default: v, ...(v ? { status: true } : {}) })}
                  />
                  <span>
                    Default template
                    <span className="block text-xs text-muted-foreground">
                      Pre-selected for new proposals. To change the default, make another template the default.
                    </span>
                  </span>
                </label>
                {usedBy > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Changes apply to new proposals only;{" "}
                    {usedBy === 1 ? "the proposal already made from it keeps its own text." : `the ${usedBy} proposals already made from it keep their own text.`}
                  </p>
                )}
              </div>

              {sampleNotes && (
                <div className="flex gap-2.5 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
                  <FileUp className="mt-0.5 size-4 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">Made from your sample</div>
                    <p className="text-xs text-muted-foreground">The look and sections are copied; the wording uses merge fields so it fits any customer. Check both tabs, then save.</p>
                    {sampleNotes.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5 text-xs">
                        {sampleNotes.map((n, i) => (
                          <li key={i} className="flex gap-1.5">
                            <Info className="mt-0.5 size-3 shrink-0 text-muted-foreground" /> {n}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button type="button" onClick={() => setSampleNotes(null)} aria-label="Hide" className="self-start text-muted-foreground hover:text-foreground">
                    <X className="size-4" />
                  </button>
                </div>
              )}

              <div className="flex gap-1 rounded-xl border bg-muted/40 p-1 text-sm" role="tablist">
                {([["sections", "Sections"], ["design", "Design"]] as const).map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    aria-selected={tab === k}
                    onClick={() => setTab(k)}
                    className={cn("flex-1 rounded-lg px-3 py-1.5 font-medium", tab === k ? "bg-card shadow-sm" : "text-muted-foreground hover:text-foreground")}
                  >
                    {label}
                    {k === "design" && draft.style && <span className="ml-1.5 inline-block size-2 rounded-full align-middle" style={{ background: draft.style.colors.accent }} />}
                  </button>
                ))}
              </div>
              {tab === "sections" ? (
                <SectionsEditor sections={draft.sections} onChange={(sections) => change({ sections })} activeEditor={activeEditor} />
              ) : (
                <TemplateDesignEditor style={draft.style} onChange={(style) => change({ style })} />
              )}
            </div>
            <div className="xl:col-span-4 xl:sticky xl:top-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-sm font-bold">
                  <Eye className="size-4 text-primary" /> Live preview
                </div>
                <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => void openPdf()} disabled={pdfBusy}>
                  {pdfBusy ? <Loader2 className="size-3.5 animate-spin" /> : <FileDown className="size-3.5" />} See as PDF
                </Button>
              </div>
              <div className="rounded-2xl border bg-muted/30 p-3">
                <p className="text-[11px] text-muted-foreground mb-2">Updates as you type, with sample customer and figures (₹50,000 + 18% GST). Nothing here is saved.</p>
                {previewError && <p className="text-xs text-destructive mb-2">Couldn't draw the preview. It will retry on your next change.</p>}
                <div className="max-h-[calc(100vh-9rem)] overflow-y-auto">
                  {preview ? <div className="[zoom:0.7]"><ProposalDocument doc={preview} /></div> : <Skeleton className="h-96" />}
                </div>
              </div>
            </div>
          </>
        ) : (
          !loading && (
            <div className="xl:col-span-9 rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">
              No templates yet. Create one to get started.
            </div>
          )
        )}
      </div>
    </div>
  );
}
